import { useEffect, useState } from 'react';
import { ApiError, get, post } from './api';

/**
 * "Browse Intercard data…" (owner 10/29, admins): every report Intercard's sales service (where the kiosk sales come
 * from) offers, what each asks for, and the answer as tables plus the raw answer. The MAC address is filled in by the
 * NAS and never shown. Only reports that only read can be run. Nothing is saved.
 */
interface Param { name: string; type: string; list: boolean; filled?: 'mac'; supported: boolean }
interface Report { name: string; doc: string; params: Param[]; canRun: boolean }
interface Table { name: string; columns: string[]; rows: string[][] }
interface Result { operation: string; status: number; ms: number; tables: Table[]; fault?: string; raw: string }

const ymd = (d: Date) => d.toISOString().slice(0, 10);
function guess(p: Param): string {
  const today = ymd(new Date(Date.now() - 7 * 3600_000));
  if (/start|from|begin/i.test(p.name) && /date/i.test(p.type + p.name + 'date')) return today;
  if (/end|to$|until/i.test(p.name)) return today;
  if (/loc/i.test(p.name)) return '8, 9, 10, 11, 12';
  return '';
}
const hint = (p: Param) => (/loc/i.test(p.name) ? 'Gresham 8, Milwaukie 9, Avalon 10, Beaverton 11, Salem 12' : p.list ? 'several allowed, with commas' : /date/i.test(p.type) || /period|date/i.test(p.name) ? 'e.g. 2026-10-29' : p.type);

export function IntercardBrowser({ showToast }: { showToast: (m: string) => void }) {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [setUp, setSetUp] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [pick, setPick] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<Result | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    get<{ reports: Report[]; setUp: boolean }>('/api/admin/apex/intercard/reports')
      .then((r) => { setReports(r.reports); setSetUp(r.setUp); })
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message : 'Couldn’t ask Intercard for its list of reports.'));
  }, []);
  const report = reports?.find((r) => r.name === pick);
  const choose = (name: string) => {
    setPick(name); setRes(null); setShowRaw(false);
    const r = reports?.find((x) => x.name === name);
    setValues(Object.fromEntries((r?.params ?? []).filter((p) => !p.filled).map((p) => [p.name, guess(p)])));
  };
  const run = async () => {
    setBusy(true); setRes(null);
    try { setRes(await post<Result>('/api/admin/apex/intercard/run', { report: pick, values })); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'Intercard didn’t answer.'); } finally { setBusy(false); }
  };

  if (loadErr) return <div style={{ fontSize: 13, background: '#FFF6D6', border: '1px solid #E9D48A', borderRadius: 6, padding: '6px 10px' }}>{loadErr}</div>;
  if (!reports) return <span style={{ fontSize: 13, color: '#4A5561' }}>Asking Intercard what it offers…</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid #D9DCD6', borderRadius: 8, padding: 12 }}>
      <span style={{ fontSize: 13, color: '#4A5561' }}>
        Everything Intercard’s sales service offers ({reports.length} report{reports.length === 1 ? '' : 's'}). Pick one, fill in the dates and locations, and press Run.
        The registered MAC address is filled in for you. Reports that might change something aren’t run from here. Nothing is saved.
        {!setUp && <b> Intercard isn’t set up on this NAS yet (INTERCARD_MAC), so reports can be listed but not run.</b>}
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {reports.map((r) => (
          <button key={r.name} className="btn2" onClick={() => choose(r.name)}
            style={{ fontWeight: pick === r.name ? 700 : 400, opacity: r.canRun ? 1 : 0.55, outline: pick === r.name ? '2px solid #1E5FA8' : undefined }}
            title={r.canRun ? r.doc || r.name : 'Not run from here'}>{r.name}</button>
        ))}
      </div>
      {report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {report.doc && <span style={{ color: '#4A5561' }}>{report.doc}</span>}
          {!report.canRun && <span style={{ background: '#FFF6D6', border: '1px solid #E9D48A', borderRadius: 6, padding: '6px 10px' }}>Not run from here: its name doesn’t say it only reads, or it asks for something this page can’t fill in. Tell Claude if you want it looked at.</span>}
          {report.params.map((p) => (
            <label key={p.name} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <b style={{ minWidth: 150 }}>{p.name}</b>
              {p.filled ? <span style={{ color: '#4A5561' }}>(registered MAC address, filled in by the NAS)</span>
                : <input className="inp" value={values[p.name] ?? ''} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })} style={{ width: 220 }} disabled={!p.supported} />}
              {!p.filled && <span style={{ color: '#7A848D', fontSize: 12 }}>{hint(p)}</span>}
            </label>
          ))}
          {report.canRun && <button className="btn" style={{ alignSelf: 'flex-start' }} disabled={busy || !setUp} onClick={() => void run()}>{busy ? 'Asking Intercard…' : 'Run'}</button>}
        </div>
      )}
      {res && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#4A5561' }}>Intercard answered {res.status} in {(res.ms / 1000).toFixed(1)} s.</span>
          {res.fault && <div style={{ fontSize: 13, background: '#FFF6D6', border: '1px solid #E9D48A', borderRadius: 6, padding: '6px 10px' }}>Intercard said: {res.fault}</div>}
          {!res.fault && !res.tables.some((t) => t.rows.length) && <span style={{ fontSize: 13 }}>No rows came back for those choices.</span>}
          {res.tables.map((t) => (
            <div key={t.name}>
              <b style={{ fontSize: 13 }}>{t.name}</b> <span style={{ fontSize: 12, color: '#4A5561' }}>({t.rows.length} row{t.rows.length === 1 ? '' : 's'})</span>
              <div style={{ overflow: 'auto', maxHeight: 480, border: '1px solid #D9DCD6', borderRadius: 6, marginTop: 4 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
                  <thead><tr>{t.columns.map((c) => <th key={c} style={{ position: 'sticky', top: 0, background: '#F1F3EF', padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #D9DCD6' }}>{c}</th>)}</tr></thead>
                  <tbody>{t.rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #EEF0EC' }}>{r.map((v, j) => <td key={j} style={{ padding: '3px 8px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', color: v === '' ? '#B0B7BE' : undefined }} title={v}>{v === '' ? '—' : v}</td>)}</tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}
          <button className="btn2" style={{ alignSelf: 'flex-start' }} onClick={() => setShowRaw((x) => !x)}>{showRaw ? 'Hide the raw answer' : 'Show the raw answer'}</button>
          {showRaw && <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 11, background: '#F7F8F6', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{res.raw}</pre>}
        </div>
      )}
    </div>
  );
}
